import JSZip from 'jszip';
import { exportToExcel } from './excelService';
import { Aggregator, AggregatorImageVariant, MenuData } from '../types';

const sanitizeName = (value: string | undefined | null, fallback = 'menu') => {
  const base = (value || '').trim().toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-') || fallback;
  return base;
};

const resolveImageUrl = (raw?: string | null) => {
  if (!raw) return null;
  if (raw.startsWith('data:')) return raw;
  if (raw.startsWith('/api/assets')) return raw;
  if (raw.includes('.r2.dev/')) return `/api/assets?key=${encodeURIComponent(raw.split('.r2.dev/')[1])}`;
  if (raw.includes('sterlingops-assets/')) return `/api/assets?key=${encodeURIComponent(raw.split('sterlingops-assets/')[1])}`;
  if (raw.startsWith('uploads/')) return `/api/assets?key=${encodeURIComponent(raw)}`;
  if (raw.startsWith('http')) return raw;
  return `data:image/png;base64,${raw}`;
};

const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const res = await fetch(dataUrl);
  return res.blob();
};

const fetchImageBlob = async (src: string): Promise<Blob | null> => {
  try {
    if (src.startsWith('data:')) {
      return await dataUrlToBlob(src);
    }
    const res = await fetch(src);
    if (!res.ok) return null;
    return await res.blob();
  } catch {
    return null;
  }
};

const blobToImageBitmap = async (blob: Blob) => {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(blob);
    } catch {
      // Fallback below
    }
  }
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });
};

const resizeBlob = async (blob: Blob, width: number, height: number, format: string, quality?: number): Promise<Blob | null> => {
  try {
    const bitmap = await blobToImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap as any, 0, 0, width, height);
    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), `image/${format}`, quality != null ? quality / 100 : undefined)
    );
  } catch {
    return null;
  }
};

const extFromBlobOrName = (blob: Blob | null, src: string, fallback = 'jpg') => {
  if (blob?.type) {
    const [, subtype] = blob.type.split('/');
    if (subtype) return subtype === 'jpeg' ? 'jpg' : subtype;
  }
  const match = src.match(/\.([a-zA-Z0-9]+)(?:$|\?|#)/);
  if (match && match[1]) return match[1].toLowerCase();
  return fallback;
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export interface MenuExportSelection {
  includeExcel: boolean;
  includeImageColumn: boolean;
  includeOriginalImages: boolean;
  folderByCategory: boolean;
  includeFlatFolder: boolean;
  aggregators: Aggregator[];
  currency: string;
  onProgress?: (payload: {
    phase: 'preparing' | 'excel' | 'images' | 'zipping' | 'finalizing';
    completed: number;
    total: number;
    imagesDone: number;
    imagesTotal: number;
    variantsDone: number;
    variantsTotal: number;
  }) => void;
  onCancelCheck?: () => boolean;
}

export interface MenuExportResult {
  success: boolean;
  totalItems: number;
  itemsWithImages: number;
  generatedVariants: number;
  failedImages: number;
  failedVariants: number;
  cancelled?: boolean;
}

export const exportMenuBundle = async (menu: MenuData, opts: MenuExportSelection): Promise<MenuExportResult> => {
  const zip = new JSZip();
  const rootFolder = sanitizeName(menu.title?.en || menu.title?.ar || 'menu', 'menu');
  const summary: MenuExportResult = {
    success: true,
    totalItems: 0,
    itemsWithImages: 0,
    generatedVariants: 0,
    failedImages: 0,
    failedVariants: 0,
  };
  const cancelled = () => opts.onCancelCheck?.() === true;

  const reportProgress = (
    phase: 'preparing' | 'excel' | 'images' | 'zipping' | 'finalizing',
    imagesDone: number,
    imagesTotal: number,
    variantsDone: number,
    variantsTotal: number
  ) => {
    const total = imagesTotal + variantsTotal || 1;
    const completed = Math.min(imagesDone + variantsDone, total);
    opts.onProgress?.({ phase, completed, total, imagesDone, imagesTotal, variantsDone, variantsTotal });
  };

  const selectedAggregators = (opts.aggregators || []).filter((agg) => agg.imageProfile?.enabled && (agg.imageProfile?.variants || []).length > 0);
  const variantColumns = selectedAggregators.flatMap((agg) => 
    (agg.imageProfile?.variants || []).map((variant) => ({
      aggKey: sanitizeName(agg.name || agg.id || 'agg', 'agg').replace(/-/g, '_'),
      variantKey: sanitizeName(variant.key, 'variant'),
      header: `image_${sanitizeName(agg.name || agg.id || 'agg', 'agg').replace(/-/g, '_')}_${sanitizeName(variant.key, 'variant')}_filename`,
    }))
  );

  const imageFilenameMap: Record<string, { original?: string; variants?: Record<string, string> }> = {};

  const categories = menu.categories || [];
  const itemsWithImageCount = categories.reduce((acc, cat) => {
    const count = (cat.items || []).filter((i) => i.image).length;
    return acc + count;
  }, 0);
  const variantsTotal = itemsWithImageCount * variantColumns.length;
  const imagesTotal = opts.includeOriginalImages ? itemsWithImageCount : 0;

  reportProgress('preparing', 0, imagesTotal, 0, variantsTotal);

  for (const cat of categories) {
    const catSegment = sanitizeName(cat.name?.en || cat.name?.ar || 'category', 'category');
    for (const item of cat.items || []) {
      if (cancelled()) {
        summary.success = false;
        summary.cancelled = true;
        reportProgress('finalizing', summary.itemsWithImages, imagesTotal, summary.generatedVariants, variantsTotal);
        return summary;
      }
      summary.totalItems += 1;
      if (!item.image) continue;
      summary.itemsWithImages += 1;
      const resolvedSrc = resolveImageUrl(item.image);
      if (!resolvedSrc) {
        summary.failedImages += 1;
        reportProgress('images', summary.itemsWithImages - summary.failedImages, imagesTotal, summary.generatedVariants, variantsTotal);
        continue;
      }
      const blob = await fetchImageBlob(resolvedSrc);
      if (!blob) {
        summary.failedImages += 1;
        reportProgress('images', summary.itemsWithImages - summary.failedImages, imagesTotal, summary.generatedVariants, variantsTotal);
        continue;
      }

      const baseName = `${sanitizeName(item.name?.en || item.name?.ar || 'item', 'item')}__${item.id}`;
      const originalExt = extFromBlobOrName(blob, resolvedSrc, 'jpg');
      const originalFile = `${baseName}.${originalExt}`;

      if (opts.includeOriginalImages) {
        const originalPath = `${rootFolder}/images/original/${opts.folderByCategory ? `${catSegment}/` : ''}${originalFile}`;
        zip.file(originalPath, blob);
        if (opts.includeFlatFolder) {
          const flatPath = `${rootFolder}/images/all_images/${originalFile}`;
          zip.file(flatPath, blob);
        }
        reportProgress('images', summary.itemsWithImages - summary.failedImages, imagesTotal, summary.generatedVariants, variantsTotal);
      }

      const variantMap: Record<string, string> = {};

      for (const agg of selectedAggregators) {
        if (cancelled()) {
          summary.success = false;
          summary.cancelled = true;
          reportProgress('finalizing', summary.itemsWithImages, imagesTotal, summary.generatedVariants, variantsTotal);
          return summary;
        }
        const aggKey = sanitizeName(agg.name || agg.id || 'agg', 'agg');
        const aggKeySafe = aggKey.replace(/-/g, '_');
        const variants = agg.imageProfile?.variants || [];
        for (const variant of variants) {
          if (cancelled()) {
            summary.success = false;
            summary.cancelled = true;
            reportProgress('finalizing', summary.itemsWithImages, imagesTotal, summary.generatedVariants, variantsTotal);
            return summary;
          }
          if (!variant.width || !variant.height || variant.width <= 0 || variant.height <= 0) {
            summary.failedVariants += 1;
            continue;
          }
          const targetExt = (variant.format || originalExt || 'jpg').toLowerCase();
          const resized = await resizeBlob(blob, variant.width, variant.height, targetExt, variant.quality);
          if (!resized) {
            summary.failedVariants += 1;
            reportProgress('images', summary.itemsWithImages - summary.failedImages, imagesTotal, summary.generatedVariants, variantsTotal);
            continue;
          }
          const variantFolder = sanitizeName(variant.label || variant.key || 'variant', 'variant');
          const variantFile = `${baseName}__${aggKeySafe}__${variant.width}x${variant.height}.${targetExt}`;
          const variantPath = `${rootFolder}/images/variants/${aggKey}/${variantFolder}/${opts.folderByCategory ? `${catSegment}/` : ''}${variantFile}`;
          zip.file(variantPath, resized);
          summary.generatedVariants += 1;
          variantMap[`${aggKeySafe}_${sanitizeName(variant.key, 'variant')}`] = variantFile;
          reportProgress('images', summary.itemsWithImages - summary.failedImages, imagesTotal, summary.generatedVariants, variantsTotal);
        }
      }

      if (opts.includeOriginalImages || Object.keys(variantMap).length) {
        imageFilenameMap[item.id] = {
          original: opts.includeOriginalImages ? originalFile : undefined,
          variants: variantMap,
        };
      }

      // micro-yield to keep UI responsive
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  if (opts.includeExcel) {
    if (cancelled()) {
      summary.success = false;
      summary.cancelled = true;
      reportProgress('finalizing', summary.itemsWithImages, imagesTotal, summary.generatedVariants, variantsTotal);
      return summary;
    }
    reportProgress('excel', summary.itemsWithImages - summary.failedImages, imagesTotal, summary.generatedVariants, variantsTotal);
    const buffer = await exportToExcel(menu, sanitizeName(menu.title?.en || 'menu-export', 'menu-export'), opts.currency, {
      includeImagesColumn: opts.includeImageColumn,
      variantColumns,
      imageFilenames: imageFilenameMap,
      returnBuffer: true,
    }) as ArrayBuffer;
    zip.file(`${rootFolder}/excel/${sanitizeName(menu.title?.en || 'menu', 'menu')}.xlsx`, buffer);
  }

  if (cancelled()) {
    summary.success = false;
    summary.cancelled = true;
    reportProgress('finalizing', summary.itemsWithImages, imagesTotal, summary.generatedVariants, variantsTotal);
    return summary;
  }

  reportProgress('zipping', summary.itemsWithImages - summary.failedImages, imagesTotal, summary.generatedVariants, variantsTotal);
  const blob = await zip.generateAsync({ type: 'blob' });
  if (!cancelled()) {
    reportProgress('finalizing', imagesTotal, imagesTotal, variantsTotal, variantsTotal);
    triggerDownload(blob, `${rootFolder}-export.zip`);
  } else {
    summary.success = false;
    summary.cancelled = true;
  }
  return summary;
};
